import {
  FIRST_NAME_INPUT_VALUE_CHANGE,
  LAST_NAME_INPUT_VALUE_CHANGE,
  EMAIL_INPUT_VALUE_CHANGE,
  PHONE_INPUT_VALUE_CHANGE,
  PASSWORD_INPUT_VALUE_CHANGE,
  USER_REGISTRATION_REQUEST_START,
  USER_REGISTRATION_REQUEST_END,
  USER_EMAIL_VERIFIED,
  SIGNIN_REQUEST_START,
  SIGNIN_REQUEST_END,
  SIGNOUT_REQUEST_START,
  SIGNOUT_REQUEST_END,
  SET_HAS_ACTIVE_CONNECTION_REQUEST_START,
  SET_HAS_ACTIVE_CONNECTION_REQUEST_END,
  SET_USER_DATA,
  PHONE_SUBMIT_REQUEST_START,
  PHONE_SUBMIT_REQUEST_END,
} from "./reducer";
import { add_app_error } from "Store/errors/thinks";
import { PARSE_APP_ID, PARSE_REST_KEY, PARSE_SERVER_URL } from "Constants";
import { is_valid_us_phone_number, normalize_us_phone_number } from "Utils";
let unsubscribe_visibility_changes = () => {};

const stop_visibility_listener = () => {
  unsubscribe_visibility_changes();
  unsubscribe_visibility_changes = () => {};
};

export const check_for_current_session = () => async (
  dispatch,
  getState,
  { Parse, StripePromise }
) => {
  Parse.User.enableUnsafeCurrentUser();

  if (Parse.User.current()) {
    const cached_user_data = Parse.User.current().toJSON();
    const query = new Parse.Query(Parse.User);

    try {
      const user_data = await query.get(cached_user_data.objectId);
      const data = user_data.toJSON();

      dispatch(
        data.emailVerified
          ? establish_user(data)
          : {
              type: SET_USER_DATA,
              payload: data,
            }
      );
    } catch (e) {
      dispatch(add_app_error(e.message));
    }
  }
};

export const set_input_value = (type) => (payload) => {
  switch (type) {
    case "first_name":
      return {
        type: FIRST_NAME_INPUT_VALUE_CHANGE,
        payload,
      };

    case "last_name":
      return {
        type: LAST_NAME_INPUT_VALUE_CHANGE,
        payload,
      };

    case "email":
      return {
        type: EMAIL_INPUT_VALUE_CHANGE,
        payload,
      };

    case "phone":
      return {
        type: PHONE_INPUT_VALUE_CHANGE,
        payload,
      };

    case "password":
      return {
        type: PASSWORD_INPUT_VALUE_CHANGE,
        payload,
      };

    default:
      return {};
  }
};

export const register_user = () => async (
  dispatch,
  getState,
  { Parse, StripePromise }
) => {
  const {
    first_name_input_value,
    last_name_input_value,
    email_input_value,
    phone_input_value,
    password_input_value,
    user,
  } = getState().user;
  const normalized_phone_number = normalize_us_phone_number(phone_input_value);
  if (!is_valid_us_phone_number(normalized_phone_number)) {
    dispatch(
      add_app_error(
        "Enter a valid U.S. phone number in the format +1 (555) 555-5555."
      )
    );
    return false;
  }

  dispatch({
    type: USER_REGISTRATION_REQUEST_START,
  });

  try {
    const new_user = new Parse.User();

    new_user.set("first_name", first_name_input_value);
    new_user.set("last_name", last_name_input_value);
    new_user.set("username", email_input_value);
    new_user.set("email", email_input_value);
    new_user.set("phone", normalized_phone_number);
    new_user.set("password", password_input_value);
    new_user.set("has_active_connection", true);

    const created_user = await new_user.signUp();

    dispatch({
      type: USER_REGISTRATION_REQUEST_END,
    });

    dispatch({
      type: SET_USER_DATA,
      payload: created_user.toJSON(),
    });
    return true;
  } catch (e) {
    dispatch({
      type: USER_REGISTRATION_REQUEST_END,
      payload: user,
    });
    dispatch(add_app_error(e.message));
    return false;
  }
};

export const check_email_verification = () => async (
  dispatch,
  getState,
  { Parse, StripePromise }
) => {
  const { objectId } = getState().user.data;
  const User = new Parse.User();
  const query = new Parse.Query(User);

  try {
    const user_data = await query.get(objectId);

    if (user_data.get("emailVerified") === true) {
      dispatch({
        type: USER_EMAIL_VERIFIED,
      });

      dispatch(log_user_in());
    } else {
      dispatch(add_app_error("Email not yet verified."));
    }
  } catch (e) {
    dispatch(add_app_error(e.message));
  }
};

export const log_user_in = () => async (
  dispatch,
  getState,
  { Parse, StripePromise }
) => {
  const { email_input_value, password_input_value, data } = getState().user;

  dispatch({
    type: SIGNIN_REQUEST_START,
  });

  try {
    const logged_in_user = await Parse.User.logIn(
      email_input_value,
      password_input_value
    );

    dispatch({
      type: SIGNIN_REQUEST_END,
    });

    dispatch(establish_user(logged_in_user.toJSON()));
  } catch (e) {
    dispatch({
      type: SIGNIN_REQUEST_END,
      payload: data,
    });

    dispatch(add_app_error(e.message));
  }
};

export const log_user_out = (history) => async (
  dispatch,
  getState,
  { Parse, StripePromise }
) => {
  const { user } = getState().user;

  dispatch({
    type: SIGNOUT_REQUEST_START,
  });

  try {
    await Parse.User.logOut();
    stop_visibility_listener();

    dispatch({
      type: SIGNOUT_REQUEST_END,
    });

    history.push("/");
  } catch (e) {
    dispatch({
      type: SIGNOUT_REQUEST_END,
      payload: user,
    });
    dispatch(add_app_error(e.message));
  }
};

export const send_password_reset_email = ({
  success_callback,
  failure_callback,
}) => async (dispatch, getState, { Parse, StripePromise }) => {
  const { email_input_value } = getState().user;

  try {
    await Parse.User.requestPasswordReset(email_input_value);

    success_callback();
  } catch (e) {
    dispatch(add_app_error(e.message));
    failure_callback();
  }
};

export const resend_verification_email = ({
  success_callback,
  failure_callback,
}) => async (dispatch, getState, { Parse, StripePromise }) => {
  const { username } = getState().user.data;
  const on_failure = (message) => {
    dispatch(add_app_error(message));
    failure_callback();
  };

  try {
    const response = await fetch(
      `${PARSE_SERVER_URL}/verificationEmailRequest`,
      {
        method: "POST",
        headers: {
          "X-Parse-Application-Id": PARSE_APP_ID,
          "X-Parse-REST-API-Key": PARSE_REST_KEY,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: username,
        }),
      }
    );
    const result = await response.json();

    if (result.error) {
      return on_failure(result.error);
    }

    success_callback();
  } catch (e) {
    on_failure(e.message);
  }
};

const set_has_active_connection = (has_active_connection) => async (
  dispatch,
  getState,
  { Parse, StripePromise }
) => {
  const { user } = getState();
  if (!user.data?.objectId) return;
  const User = new Parse.User();
  const query = new Parse.Query(User);

  dispatch({
    type: SET_HAS_ACTIVE_CONNECTION_REQUEST_START,
    payload: has_active_connection,
  });

  try {
    const user_data = await query.get(user.data.objectId);

    user_data.set("has_active_connection", has_active_connection);

    await user_data.save();

    dispatch({
      type: SET_HAS_ACTIVE_CONNECTION_REQUEST_END,
      payload: has_active_connection,
    });
  } catch (e) {
    dispatch(add_app_error(e.message));

    dispatch({
      type: SET_HAS_ACTIVE_CONNECTION_REQUEST_END,
      payload: has_active_connection,
    });
  }
};

const establish_user = (user_data) => (dispatch, getState) => {
  var vis = (function () {
    var stateKey,
      eventKey,
      keys = {
        hidden: "visibilitychange",
        webkitHidden: "webkitvisibilitychange",
        mozHidden: "mozvisibilitychange",
        msHidden: "msvisibilitychange",
      };

    for (stateKey in keys) {
      if (stateKey in document) {
        eventKey = keys[stateKey];
        break;
      }
    }

    return function (callback) {
      if (callback) {
        if (!eventKey) return () => {};
        document.addEventListener(eventKey, callback);
        return () => document.removeEventListener(eventKey, callback);
      }
      return !document[stateKey];
    };
  })();
  const connect_user = () => dispatch(set_has_active_connection(true));
  const disconnect_user = () => dispatch(set_has_active_connection(false));
  stop_visibility_listener();

  dispatch({
    type: SET_USER_DATA,
    payload: user_data,
  });

  connect_user();

  unsubscribe_visibility_changes = vis(() =>
    vis() === true ? connect_user() : disconnect_user()
  );
};

export const submit_phone = (phone) => async (
  dispatch,
  getState,
  { Parse, StripePromise }
) => {
  const { user } = getState();
  const normalized_phone_number = normalize_us_phone_number(phone);
  if (!is_valid_us_phone_number(normalized_phone_number)) {
    dispatch(
      add_app_error(
        "Enter a valid U.S. phone number in the format +1 (555) 555-5555."
      )
    );
    return false;
  }
  const User = new Parse.User();
  const query = new Parse.Query(User);

  dispatch({
    type: PHONE_SUBMIT_REQUEST_START,
    payload: normalized_phone_number,
  });

  try {
    const user_data = await query.get(user.data.objectId);

    user_data.set("phone", normalized_phone_number);

    await user_data.save();

    dispatch({
      type: PHONE_SUBMIT_REQUEST_END,
      payload: normalized_phone_number,
    });
    return true;
  } catch (e) {
    dispatch(add_app_error(e.message));

    dispatch({
      type: PHONE_SUBMIT_REQUEST_END,
      payload: normalized_phone_number,
    });
    return false;
  }
};
